import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { useIsProjectAdmin } from '@/features/auth/use-permission';
import { ForbiddenPage } from '@/components/shared/ForbiddenPage';
import { GenerateReportView } from '@/features/report/GenerateReportView';
import { ImportExcelView } from '@/features/report/ImportExcelView';
import { TemplateView } from '@/features/report/TemplateView';
import { WeeksView } from '@/features/report/WeeksView';

const REPORT_SECTIONS = ['import', 'template', 'weeks', 'generate'] as const;
type ReportSection = (typeof REPORT_SECTIONS)[number];

const reportSearchSchema = z.object({
  section: z.enum(REPORT_SECTIONS).optional(),
});

export const Route = createFileRoute('/_authed/projects/$projectId/report')({
  validateSearch: reportSearchSchema,
  component: ReportPage,
});

const SUB_TABS: { key: ReportSection; label: string }[] = [
  { key: 'import', label: 'Import Data' },
  { key: 'template', label: 'Template' },
  { key: 'weeks', label: 'Weeks' },
  { key: 'generate', label: 'Generate Report' },
];

function ReportPage() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const isAdmin = useIsProjectAdmin(projectId);
  const active: ReportSection = search.section ?? 'import';

  if (!isAdmin) return <ForbiddenPage />;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1 border-b border-border px-4">
        {SUB_TABS.map((tab) => {
          const isActive = tab.key === active;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() =>
                void navigate({
                  search: { section: tab.key === 'import' ? undefined : tab.key },
                })
              }
              className={cn(
                'relative h-10 px-3 text-sm font-medium transition-colors',
                isActive ? 'text-primary-deep' : 'text-text-muted hover:text-text',
              )}
            >
              {tab.label}
              {isActive ? (
                <span
                  aria-hidden="true"
                  className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-primary"
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {active === 'import' ? (
          <div className="p-4 md:p-6">
            <ImportExcelView projectId={projectId} />
          </div>
        ) : null}
        {active === 'template' ? (
          <div className="p-4 md:p-6">
            <TemplateView />
          </div>
        ) : null}
        {active === 'weeks' ? (
          <div className="p-4 md:p-6">
            <WeeksView projectId={projectId} />
          </div>
        ) : null}
        {active === 'generate' ? (
          <div className="p-4 md:p-6">
            <GenerateReportView projectId={projectId} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
